/**
 * BudgetRepo sobre Supabase. Misma lógica que DexieBudgetRepo.
 */

import type { BudgetRepo } from "../../repos/BudgetRepo";
import type { Budget } from "@app-types/entities";
import type { EstadoBudget } from "@constants/estados";
import { GERENCIA_DEFAULT } from "@constants/gerencias";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

function normalizar(b: Budget): Budget {
  if (b.gerenciaId) return b;
  return { ...b, gerenciaId: GERENCIA_DEFAULT };
}

export class SupabaseBudgetRepo extends SupabaseBaseRepo<Budget> implements BudgetRepo {
  constructor(tenantId: string) {
    super("budget", tenantId);
  }

  async list(filter?: Partial<Budget>): Promise<Budget[]> {
    let res = (await this.listAll()).map(normalizar);
    if (filter?.estado) res = res.filter((b) => b.estado === filter.estado);
    if (filter?.gerenciaId) res = res.filter((b) => b.gerenciaId === filter.gerenciaId);
    return res.sort((a, b) => a.fechaInicio.localeCompare(b.fechaInicio));
  }

  async listByEstado(estado: EstadoBudget): Promise<Budget[]> {
    return (await this.listAll()).filter((b) => b.estado === estado).map(normalizar);
  }

  async getActivo(): Promise<Budget | null> {
    return (await this.listByEstado("ACTIVO"))[0] ?? null;
  }

  async get(id: string): Promise<Budget | null> {
    const item = await this.getById(id);
    return item ? normalizar(item) : null;
  }

  async create(data: Omit<Budget, "id" | "createdAt" | "updatedAt" | "version">): Promise<string> {
    const now = new Date().toISOString();
    const nuevo: Budget = {
      ...data,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    await this.createRaw(nuevo);
    return nuevo.id;
  }

  async update(id: string, data: Partial<Budget>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Budget ${id} not found`);
    const updated: Budget = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }
}
